import { useRef, useCallback } from 'react';

/**
 * Custom hook for preserving scroll position during async operations
 * Provides a more reliable way to maintain scroll position than setTimeout
 */
export function useScrollPreservation() {
  const scrollPositionRef = useRef<number>(0);
  const isPreservingRef = useRef<boolean>(false);
  
  /**
   * Captures the current scroll position
   */
  const captureScroll = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
    isPreservingRef.current = true;
  }, []);
  
  /**
   * Restores the captured scroll position immediately
   * Uses multiple strategies to ensure scroll is preserved without jumping
   */
  const restoreScroll = useCallback(() => {
    if (!isPreservingRef.current) return;
    
    const targetScroll = scrollPositionRef.current;
    
    // Strategy 1: Immediate restoration (prevents visible jump)
    window.scrollTo(0, targetScroll);
    
    // Strategy 2: Single RAF for any delayed DOM updates
    requestAnimationFrame(() => {
      if (window.scrollY !== targetScroll) {
        window.scrollTo(0, targetScroll);
      }
      isPreservingRef.current = false;
    });
  }, []);
  
  /**
   * Wraps an async function to preserve scroll position
   * Captures scroll before execution and restores after
   */
  const preserveScrollDuring = useCallback(<T extends any[], R>(
    fn: (...args: T) => R | Promise<R>
  ) => {
    return async (...args: T): Promise<R> => {
      captureScroll();
      
      try {
        const result = await fn(...args);
        restoreScroll();
        return result;
      } catch (error) {
        restoreScroll();
        throw error;
      }
    };
  }, [captureScroll, restoreScroll]);
  
  /**
   * Prevents default scroll behavior on focus
   * Useful for preventing automatic scrolling when elements receive focus
   */
  const preventFocusScroll = useCallback((element: HTMLElement | null) => {
    if (!element) return;
    
    // Store original scrollTop
    const originalScrollY = window.scrollY;
    
    // Add listener to prevent scroll on focus
    const handleFocus = (e: FocusEvent) => {
      // Prevent the default scroll behavior
      window.scrollTo(0, originalScrollY);
    };
    
    element.addEventListener('focus', handleFocus, { once: true });
    
    // Clean up after a short delay
    setTimeout(() => {
      element.removeEventListener('focus', handleFocus);
    }, 100);
  }, []);
  
  return {
    captureScroll,
    restoreScroll,
    preserveScrollDuring,
    preventFocusScroll
  };
}